import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Trans, useLingui } from "@lingui/react/macro";
import { useTypedSelector } from "../hooks";
import GoogleSignInButton from "../components/common/GoogleSignInButton";
import EconomicSignInButton from "../components/common/EconomicSignInButton";
import MicrosoftSignInButton from "../components/common/MicrosoftSignInButton";
import { BASE_URL } from "../const";
import { tallyTokens } from "../theme.tsx";
import CreatePartnerCompanyDialog from "../components/partner/CreatePartnerCompanyDialog";
import {
  PartnerCompany,
  createPartnerApiClient,
  fetchPartnerCompanies,
  fetchPartnerLoginAsUrl,
  storePartnerSession,
} from "../components/partner/partnerApi";

const PartnerPage: React.FC = () => {
  const { t } = useLingui();
  const isAuthenticated = useTypedSelector(
    (state) => state.authState.isLoggedIn,
  );

  const [companies, setCompanies] = useState<PartnerCompany[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [companiesError, setCompaniesError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);

  const [loginAsLoading, setLoginAsLoading] = useState<string | null>(null);
  const [loginAsError, setLoginAsError] = useState<string | null>(null);

  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    setCompaniesError(null);
    setAccessDenied(false);
    try {
      const partnerApi = createPartnerApiClient();
      setCompanies(await fetchPartnerCompanies(partnerApi));
    } catch (error: any) {
      if (error?.response?.status === 403) {
        setAccessDenied(true);
      } else {
        setCompaniesError(t`Failed to load companies.`);
      }
    } finally {
      setLoadingCompanies(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchCompanies();
    }
  }, [isAuthenticated]);

  const handleLoginAs = async (tenantId: string) => {
    setLoginAsLoading(tenantId);
    setLoginAsError(null);
    try {
      const partnerApi = createPartnerApiClient();
      const url = await fetchPartnerLoginAsUrl(partnerApi, tenantId);
      if (url) {
        storePartnerSession();
        window.location.href = url;
      } else {
        setLoginAsError(t`No login URL returned.`);
      }
    } catch (error: any) {
      if (error?.response?.status === 403) {
        setLoginAsError(
          t`You do not have permission to log in as this company.`,
        );
      } else {
        setLoginAsError(t`Failed to log in as company.`);
      }
    } finally {
      setLoginAsLoading(null);
    }
  };

  const handleSSOLogin = (provider: string) => {
    localStorage.setItem("returnTo", "/partner");
    const endpoints: Record<string, string> = {
      google: "/auth/google-login",
      "visma-connect": "/v1/auth/visma-connect-login",
      microsoft: "/v1/auth/microsoft-login",
    };
    window.location.href = BASE_URL + endpoints[provider];
  };

  if (!isAuthenticated) {
    return (
      <Container
        maxWidth="md"
        sx={{ mt: 4, color: tallyTokens.ink, textAlign: "center" }}
      >
        <Typography variant="h4" gutterBottom>
          <Trans context="Partner portal page title">Partner Portal</Trans>
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          <Trans context="Partner page login prompt">
            Sign in to access the partner portal.
          </Trans>
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <GoogleSignInButton
            onClick={() => handleSSOLogin("google")}
            text="Sign in with Google"
          />
          <EconomicSignInButton
            onClick={() => handleSSOLogin("visma-connect")}
          />
          <MicrosoftSignInButton onClick={() => handleSSOLogin("microsoft")} />
        </Box>
      </Container>
    );
  }

  if (accessDenied) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          <Trans context="Error message when user lacks partner permissions">
            You do not have partner access.
          </Trans>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, color: tallyTokens.ink }}>
      <Typography variant="h4" gutterBottom>
        <Trans context="Partner portal page title">Partner Portal</Trans>
      </Typography>

      <Box mb={2}>
        <Button variant="contained" onClick={() => setDialogOpen(true)}>
          <Trans context="Partner portal button and dialog title for creating a company">
            Create Company
          </Trans>
        </Button>
      </Box>

      {loginAsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loginAsError}
        </Alert>
      )}

      {loadingCompanies ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : companiesError ? (
        <Alert severity="error">{companiesError}</Alert>
      ) : (
        <Table sx={{ "& .MuiTableCell-root": { color: "inherit" } }}>
          <TableHead>
            <TableRow>
              <TableCell>
                <Trans context="Partner portal company name column and form label">
                  Company Name
                </Trans>
              </TableCell>
              <TableCell>
                <Trans context="Partner portal table column header for creation date">
                  Created
                </Trans>
              </TableCell>
              <TableCell>
                <Trans context="Partner portal button to log in as a company">
                  Login As
                </Trans>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {companies.map((company) => (
              <TableRow key={company.tenant_id}>
                <TableCell>{company.name}</TableCell>
                <TableCell>
                  {new Date(company.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={loginAsLoading === company.tenant_id}
                    onClick={() => handleLoginAs(company.tenant_id)}
                  >
                    {loginAsLoading === company.tenant_id ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Trans context="Partner portal button to log in as a company">
                        Login As
                      </Trans>
                    )}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {companies.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Trans context="Partner portal empty state message">
                    No companies found.
                  </Trans>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <CreatePartnerCompanyDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={fetchCompanies}
      />
    </Container>
  );
};

export default PartnerPage;
